
import React, { useState } from 'react';

import { MSGSidebar } from "@/app/pages/msg/sidebar";
import { MSGMobileNavigation } from "@/app/pages/msg/mobile-navigation";
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { TourAlertDialog, useTour } from '@/components/tour';


import {
  OverviewSection,

  // Context
  InternalAndExternalIssuesSection,
  InterestedPartiesSection,
  Scope,
  MMS,
  LegalObligationsSection,
  SpecialInterestGroupsSection,

  // Leadership
  LeadershipCommitment,
  Policy,
  LeadershipRoles,

  // Planning
  PlanningGeneral,
  Changes,
  Objectives,
  RiskAssessment,
  RiskTreatment,

  Communication,
  Documentation,
  Roles,
  CompetencyOverview,
  AssetOwner,
  TopManagement,
  SupportUser,
  BusinessSponsor,

  // Operation
  OperationOverview,

  // Performance
  Monitoring,
  InternalAudit,
  ManagementReview,

  ImprovementsOverview,
} from '@/app/pages/msg/sections';

const tabContent = {
  'overview': <OverviewSection />,

  // Context section
  'context': <InternalAndExternalIssuesSection />, // Default view for context
  'context.issues': <InternalAndExternalIssuesSection />,
  'context.parties': <InterestedPartiesSection />,
  'context.scope': <Scope />,
  'context.mms': <MMS />,
  'context.legal': <LegalObligationsSection />,
  'context.groups': <SpecialInterestGroupsSection />,

  // Leadership section
  'leadership': <LeadershipCommitment />, // Default view for leadership
  'leadership.commitment': <LeadershipCommitment />,
  'leadership.policy': <Policy />,
  'leadership.roles': <LeadershipRoles />,

  // Planning section
  'planning': <PlanningGeneral />, // Default view for planning
  'planning.general': <PlanningGeneral />,
  'planning.changes': <Changes />,
  'planning.objectives': <Objectives />,
  'planning.risk-assessment': <RiskAssessment />,
  'planning.risk-treatment': <RiskTreatment />,

  // Support section
  'support': <CompetencyOverview />, // Default view for support
  'support.communication': <Communication />,
  'support.documentation': <Documentation />,
  'support.roles': <Roles />,
  'support.competency': <CompetencyOverview />, // Default view for competency
  'support.competency.overview': <CompetencyOverview />,
  'support.competency.assetOwner': <AssetOwner />,
  'support.competency.top-management': <TopManagement />,
  'support.competency.user': <SupportUser />,
  'support.competency.business-sponsor': <BusinessSponsor />,

  // Operation section
  'operation': <OperationOverview />,
  'operation.overview': <OperationOverview />,

  'performance.monitoring': <Monitoring />,
  'performance.internalaudit': <InternalAudit />,
  'performance.managementreview': <ManagementReview />,

  'improvements': <ImprovementsOverview />,
};



import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { MSGReadOnlyProvider } from './contexts/ReadOnlyContext';
import { MSGClausesProvider, useMSGClauses } from './contexts/ClausesContext';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { useTenancy } from '@/contexts/TenancyContext';

interface ManagementSystemGuideProps {
  basePath?: string;
  isReadOnly?: boolean;
}

const ManagementSystemGuide: React.FC<ManagementSystemGuideProps> = ({ basePath = '/msg', isReadOnly = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract the active tab from the URL path, relative to basePath
  const basePathSegments = basePath.split('/').filter(Boolean);
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Find the index where basePath ends in the current path
  const baseIndex = pathSegments.findIndex((seg, i) =>
    basePathSegments.every((bSeg, j) => pathSegments[i + j] === bSeg)
  );

  let relevantSegments = [];
  if (baseIndex !== -1) {
    relevantSegments = pathSegments.slice(baseIndex + basePathSegments.length);
  }
  const activeTabKey = relevantSegments.length > 0 ? relevantSegments.join('.') : 'overview';

  // Function to handle tab changes
  // In /src/app/pages/msg/index.tsx
  const handleTabChange = (tabKey: string) => {
    const parts = tabKey.split('.');
    let newPath = basePath;

    // Handle each part of the path
    parts.forEach(part => {
      newPath += `/${part}`;
    });

    navigate(newPath);
  };

  // Set the default tab if no valid tab is active
  useEffect(() => {
    const rootTab = activeTabKey.split('.')[0];
    if (!tabContent[activeTabKey] && !tabContent[rootTab] && rootTab !== 'msg') {
      navigate(`${basePath}/overview`, { replace: true });
    }
  }, [activeTabKey, navigate]);

  // Tour state
  const { setSteps, isTourCompleted, setTourPage } = useTour();
  const [openTour, setOpenTour] = useState(false);

  // Tour implementation
  useEffect(() => {
    setTourPage('system-guide');
  }, [setTourPage]);

  // Tour configuration for System Guide (MSG)
  useEffect(() => {
    const steps: any[] = [
      {
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">System Guide</h3>
            <p>This is your system guide page. It shows you all the system guides in your tenant.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.SYSTEM_GUIDE_PAGE,
        position: "bottom" as const,
      },
      {
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">MSG sidebar</h3>
            <p>This is how you navigate your MSG.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.MSG_SIDEBAR,
        position: "right" as const,
      }
    ];

    console.log('System Guide tour steps:', steps.length);
    setSteps(steps);

    // Auto-start tour if not completed
    if (!isTourCompleted) {
      setOpenTour(true);
    }
  }, [setSteps, isTourCompleted, setTourPage]);

  // Get the component to render based on the current route
  const getActiveComponent = () => {
    // First try exact match
    if (tabContent[activeTabKey]) {
      return tabContent[activeTabKey];
    }

    // Then try to find a parent component
    const parts = activeTabKey.split('.');
    while (parts.length > 0) {
      parts.pop(); // Remove the last part
      const parentKey = parts.join('.');
      if (tabContent[parentKey]) {
        return tabContent[parentKey];
      }
    }

    // Default fallback
    return <OverviewSection />;
  };
  const MCMMsgMappingsCardSection: React.FC = () => {
    const { activeTenancy } = useTenancy();
    const clausesCtx = useMSGClauses();
    const controlIds = React.useMemo(() => Array.from(clausesCtx?.clauses || new Set<string>()).map(String), [clausesCtx?.clauses]);
    return (
      <div className="mt-6">
        <MCMMappingsCard
          tenancyId={activeTenancy?.id}
          isReadOnly={isReadOnly}
          controlIds={controlIds}
          maxWidth="none"
          collapsible
        />
      </div>
    );
  };
  return (
    <MSGReadOnlyProvider isReadOnly={isReadOnly}>
      <div id={TOUR_STEP_IDS.SYSTEM_GUIDE_PAGE} className="settings-page-wrapper flex w-full h-full min-h-screen">
        <div className="hidden md:block w-80 border-r border-border bg-card h-full min-h-screen sticky top-0">
          <div id={TOUR_STEP_IDS.MSG_SIDEBAR}>
            <MSGSidebar active={activeTabKey} onTabChange={handleTabChange} />
          </div>
        </div>
        <div className="flex-1 flex flex-col bg-background min-h-screen">
          <div className="settings-page-content w-full max-w-7xl mx-auto px-6 mt-12 pb-24 md:pb-12">
            <h1 className="text-3xl font-bold mb-2 text-foreground text-center pb-2">Management System Guide</h1>
            <div className="w-full">
              <MSGClausesProvider>
                {getActiveComponent()}
                <MCMMsgMappingsCardSection />
              </MSGClausesProvider>
            </div>
          </div>
        </div>
      </div>
      <MSGMobileNavigation active={activeTabKey} onTabChange={handleTabChange} />
      <TourAlertDialog 
        isOpen={openTour} 
        setIsOpen={setOpenTour} 
      />
    </MSGReadOnlyProvider>
  );
};

export default ManagementSystemGuide;
